import { useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import API from '../../../utils/API';
import MesTable from '../../../components/Table/MesTable';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import constants from '../../../utils/constants';
import commonMethods from '../../../utils/common-methods';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import editIcon from '../../../assets/icons/table/edit.svg';
import trashIcon from '../../../assets/icons/table/trash.svg';
import deleteDrafts from '../../../assets/icons/signage-plus/delete-drafts.svg';
import NotificationModal from '../../../components/MessageManager/NotificationModal';
import { signageRequested } from '../../../store/signage';

function SignageDrafts(props) {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map(permission => permission.id);

  if (!(permissionIds.includes(constants.permissionIds.signagePlusOwner) || permissionIds.includes(constants.permissionIds.signagePlusTeacher))) {
    navigate('/forbidden');
  }
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('dateAndTime');
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [actionType, setActionType] = useState('');

  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const modalRef = useRef(null);

  const [notificationProps, setNotificationProps] = useState({
    icon: deleteDrafts,
    message: 'ML_REMOVE_TIPS',
    description: 'REMOVE_DRAFTS',
    showSecondaryButton: true,
    primaryButtonText: 'CONTINUE',
    className: 'red'
  });

  useEffect(() => {
    modalRef.current = new window.bootstrap.Modal(document.getElementById('notificationModal'));
  }, []);

  useEffect(() => {
    setNotificationProps({
      icon: deleteDrafts,
      message: 'ML_REMOVE_TIPS',
      description: 'REMOVE_DRAFTS',
      showSecondaryButton: true,
      primaryButtonText: 'CONTINUE',
      className: 'red',
      onSubmit: (e) => {
        handleDeleteEvent(e);
        modalRef.current.hide();
      }
    });
  }, [actionType, selectedIds]);

  const handleDeleteEvent = async (e) => {
    e.preventDefault();
    try {
      await API.deleteSignages(Object.keys(selectedIds));
      loadTable(dataMap);
      modalRef.current.hide();
      setSuccess(<FormattedMessage id="SIGNAGE_DELETED_SUCCESSFULLY" />);
    } catch (error) {
      setError(<FormattedMessage id="SIGNAGE_DELETED_FAILED" values={{ errorMsg: error.message }} />);
      modalRef.current.hide();
    }
  };

  const loadTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const res = await API.getSignages('draft');
      res.data.forEach((mess) => {
        map.data[mess.id] = {
          id: mess.id,
          message: mess.title,
          user: `${mess.user?.firstName || ''} ${mess.user?.lastName || ''}`,
          lastModified: commonMethods.formatUtcDate(mess.lastModified),
          status: mess.status,
          checked: false,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setDataCount(map.ids.length);
      setSelectedIds(map.selectedIds);
      setTotalDataCount(map.ids.length);
      setHistoryData(res.data);
      setDataLoaded(true);
    } catch (err) {
      setError(err.message);
      setDataLoaded(true);
    }
  };

  const dataMap = useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
    loadTable(map);
    return map;
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, historyData]);

  const handleCreateSignageTabClick = (e, row = null, current = 0) => {
    e.stopPropagation();
    if (row) {
      props.dispatch(signageRequested({ id: row.original.id, current: current, menuid: 'signagePlusDraftsTab' }));
    }
    document.getElementById('signagePlusCreateSignageTab').click();
  }

  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: 'checkbox',
      accessor: 'checked',
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: 'DRAFT_NAME',
      id: 'message',
      accessor: 'message',
      isVisible: true,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
              <span className="text-decoration-underline cursor-pointer" style={{whiteSpace: 'pre', overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={(e) => handleCreateSignageTabClick(e, row, 1)}>{row.original.message}</span>
        </div>
      ),
    },
    {
      Header: 'CREATED_BY',
      id: 'user',
      accessor: 'user',
      isVisible: true,
    },
    {
      Header: 'LAST_EDITED',
      id: 'lastModified',
      isVisible: true,
      accessor: 'lastModified',
    },
    {
      Header: '',
      id: 'actions',
      isVisible: true,
      mWidth: "mWidth-125",
      Cell: ({ row }) => (
        <div className="actions d-flex align-items-center">
          <button
            className="btn"
            id="editBtn"
            onClick={(e) => handleCreateSignageTabClick(e, row, 0)}
          >
            <img src={editIcon} className="actionIcon editAction" alt={<FormattedMessage id="EDIT" />} />
          </button>
          <span className="vertical-divider"></span>
          <button
            className="btn red"
            id="deleteBtns"
            data-bs-toggle="modal"
            data-bs-target="#notificationModal"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIds(JSON.parse(`{"${row.original.id}": 1}`));
            }}
          >
            <img src={trashIcon} className="actionIcon deleteAction" alt={<FormattedMessage id="DELETE" />} />
          </button>
        </div>
      ),
    },
  ];

  const buttons = {
    toolbarRightButtons: (
      <>
        <button
          id="createBtn"
          type="button"
          className="btn btn-primary but-darkblue fw-normal"
          onClick={(e) => handleCreateSignageTabClick(e)}
        >
          <FormattedMessage id="CREATE_NEW_SIGNAGE" />
        </button>
      </>
    ),
    footerButtons: (
      <>
        <button
          id="deleteBtn"
          type="button"
          className="btn deleteBtn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={Object.keys(selectedIds).length === 0}
        >
          <FormattedMessage id="DELETE" defaultMessage="Delete" />
        </button>
      </>
    ),
  }

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbds_drafts_table",
    pageType: constants.pageType.Drafts,
    modalId: "Drafts",
    columns: tableColumnArray,
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success, setSuccess,
    error, setError,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons
  };

  return (
    <>
      <NotificationModal {...notificationProps} />
      <MesTable {...tableProps} />
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user
});


export default connect(mapStateToProps)(SignageDrafts)
